import { forwardRef, useImperativeHandle, useMemo } from 'react';
import TOAST from 'consts/toast.consts';
import ChatContentItem from 'views/chat/components/items/ChatContentItem.component';
import ChatActionEmojisItem from 'views/chat/components/items/ChatActionEmojisItem.component';
import ChatActionAttachmentFileItem from 'views/chat/components/items/ChatActionAttachmentFileItem.component';
import ChatActionQuickReplyItem from './ChatActionQuickReplyItem.component';
import ChatActionInputItem from 'views/chat/components/items/ChatActionInputItem.component';
import ChatActionButtonSendMessageItem from 'views/chat/components/items/ChatActionButtonSendMessageItem.component';
import ChatContentCommentIconItem from 'views/chat/components/items/ChatContentCommentIconItem.component';
import NeoInputFile from 'design/design_components/neo/form/NeoInputFile.base';
import NeoProgressBar from 'design/design_components/neo/overlay/NeoProgressBar.base';
import NeoButtonMain from 'design/design_components/neo/button/NeoButtonMain.base';
import NeoTitleMain from 'design/design_components/neo/title/NeoTitleMain.base';
import useMessages from 'hooks/useMessages.hook';
import useComponent from 'hooks/useComponent.hook';
import useChat from 'hooks/chat/useChat.hook';
import ChatService from 'services/chat.service';
import useAuthentication from 'hooks/useAuthentication.hook';
import reactStringReplace from 'react-string-replace';
import NeoInputText from 'design/design_components/neo/form/NeoInputText.base';
import InternalProgressBarSpinner from 'components/InternalProgressBarSpinner.component';
import { useChatEvents } from 'hooks/chat/useChatEvents.hook';
import { useChatQuicklyReplies } from 'hooks/chat/useChatQuicklyReplies.hook';

const FILTER_ORDER = {
  ASC: 1
};

const LIMIT_FILES_BY_REQUEST = 5;

const orderConversationContentByRecentAsc = (events) => {
  return [...events].sort((eventA, eventB) => {
    const A = 1;
    const B = -1;
    if (eventA.timestamp === eventB.timestamp) {
      if ((eventA.type === 'EVENT' && eventB.type === 'COMMENT')
        || (eventA.type === 'TEMPLATE' && eventB.type === 'EVENT')
        || ((eventA.type !== 'EVENT' && eventA.type !== 'COMMENT' && eventA.type !== 'TEMPLATE') && eventB.type === 'EVENT')
      ) return B;
      return A;
    } else {
      return new Date(eventB.timestamp) - new Date(eventA.timestamp);
    }
  }).reverse();
}

const Conversation = forwardRef((props, ref) => {
  const chat = useChat();
  const authentication = useAuthentication();
  const { data: events, loading: eventsIsLoading } = useChatEvents({ lineId: chat.selected.conversation.lineId, chatId: chat.selected.conversation.chatId });
  const { data: quicklyReplies } = useChatQuicklyReplies( { enabled: authentication.user?.role === 'AGENT' } );
  
  const messages = useMessages();
  const component = useComponent({
    state: {
      element: {
        content: {
          filterOrderActive: FILTER_ORDER.ASC
        },
        attachmentFile: {
          show: false,
          uploading: false,
          progress: 0
        },
        addMessage: {
          input: {
            value: ''
          }
        },
        quicklyReply: {
          show: false,
          search: '',
          filtered: []
        }
      }
    }
  });

  const element = {
    ref: component.useRef(ref),
    loading: {
      ref: component.useRef(null)
    },
    content: {
      ref: component.useRef(null)
    },
    attachmentFile: {
      ref: component.useRef(null)
    },
    notice: {
      ref: component.useRef(null)
    },
    addMessage: {
      ref: component.useRef(null),
      input: {
        ref: component.useRef(null)
      }
    }
  }

  const applyFiltersEvents = (events = [], order = FILTER_ORDER.ASC) => {
    switch (order) {
      case FILTER_ORDER.ASC:
        events = orderConversationContentByRecentAsc(events);
        break;
    }
    return events;
  }

  const filteredEvents = useMemo(() => 
    applyFiltersEvents(events, component.state.element.content.filterOrderActive)
  , [events, component.state.element.content.filterOrderActive]);
  
  component.useEffect(() => {
    if (element.content.ref.current && filteredEvents.length > 0) {
      contentScrollBottom();
    }
  }, [element.content.ref.current, filteredEvents])

  component.useEffect(() => {
    component.setState((state) => {
      state.element.quicklyReply.filtered = quicklyReplies.filter(({title, replie}) => {
        return component.state.element.quicklyReply.search === '' 
          || title.toLowerCase().includes(component.state.element.quicklyReply.search.toLowerCase()) || replie.toLowerCase().includes(component.state.element.quicklyReply.search.toLowerCase());
      });
    })
  }, [quicklyReplies, component.state.element.quicklyReply.search])

  useImperativeHandle(ref, () => ({
    content: {
      scrollBottom: contentScrollBottom
    }
  }));

  const contentScrollBottom = () => {
    const contentElement = element.content.ref.current;
    if (contentElement) {
      const scrollHeightOfContent = contentElement.scrollHeight;
      contentElement.scrollTo(0, scrollHeightOfContent);

      if (authentication.user.role === 'AGENT'
        && Boolean(chat.selected.conversation?.newMessages)
        && !chat.selected.conversation?.isExpired
        && !chat.selected.conversation?.isFinished
      ) {
        chat.readMessagesInConversation({
          conversation: chat.selected.conversation
        });
      }
    }
  }

  const attachmentFileUploadHandler = async ({ files }) => {
    component.setState((state) => {
      state.element.attachmentFile.uploading = true;
    });
    const filesProgress = [];
    const filesResponses = await Promise.all(files.map(async (file) => {
      filesProgress[file.name] = 0;
      const onUploadProgress = (progressEvent) => {
        const countFiles = files.length;
        filesProgress[file.name] = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        const totalOfUpload = Object.values(filesProgress).reduce((previous, current) => previous + current, 0) / countFiles;
        component.setState((state) => {
          state.element.attachmentFile.progress = totalOfUpload;
        });
      }
      const response = await ChatService.mediaFileToSend({ file }, onUploadProgress);
      const message = response.payload;
      const { type, ...contentOfMessage } = message;
      return await chat.sendMessage(contentOfMessage, type);
    }));
    element.attachmentFile.ref?.current?.clear();
    component.setState((state) => {
      state.element.attachmentFile.show = false;
      state.element.attachmentFile.uploading = false;
      state.element.attachmentFile.progress = 0;
    });
  }

  const loading = (props.render.loading.isRender && eventsIsLoading ) &&
    <div 
      className={`${props.component.name}__conversation__loading`}
      ref={element.loading.ref}
    >
      <InternalProgressBarSpinner />
    </div>;

  const content = (props.render.content.isRender) && 
    <div 
      className={`${props.component.name}__conversation__content`} 
      ref={element.content.ref}
    >
      {filteredEvents.map((event, key) => <ChatContentItem key={key} event={event}/>)}
    </div>;

  const attachmentFile = (props.render.attachmentFile.isRender) && <div className={`${props.component.name}__conversation__attachment-file ${component.state.element.attachmentFile.show ? 'show' : 'hide'}`}>
    <div className={`${props.component.name}__conversation__attachment-file-content`}>
      <div className={`${props.component.name}__conversation__attachment-file-content-header`}>
        {component.state.element.attachmentFile.uploading
          ? <NeoProgressBar
            value={component.state.element.attachmentFile.progress}
            style={{ width: `${component.state.element.attachmentFile.progress}%` }} />
          : <span>Carga de archivos</span>
        }
        <i className="pi pi-times" style={{ 'fontSize': '1.5em' }} onClick={() => {component.setState((state) => {state.element.attachmentFile.show = false;})}}></i>
      </div>
      <NeoInputFile
        ref={element.attachmentFile.ref}
        extra={props.showAttachmentFile ? 'back' : 'forth'}
        maxFileSize={200000000}
        customUpload
        uploadHandler={attachmentFileUploadHandler}
        multiple={true}
        emptyTemplate={<p>O arrástralos aquí</p>}
        chooseLabel={"Carga tus archivos"}
        onSelect={(event) => {
          const inputFile = element.attachmentFile.ref?.current;
          if (inputFile) {
            if (inputFile.files.length > LIMIT_FILES_BY_REQUEST) {
              messages.showToast(TOAST.SEVERITY.WARNING, 'Demasiados archivos', `Solamente está permitido subir ${LIMIT_FILES_BY_REQUEST} archivos por petición`);
              inputFile.clear();
            }
          }
        }}
        previewWidth={0}
        cancelOptions={{
          className: 'p-d-none'
        }}
        uploadOptions={{
          className: 'p-d-none'
        }}
      />
      <div className={`${props.component.name}__conversation__attachment-file-content-footer`}>
        <NeoButtonMain
          type="button"
          icon="fa-regular fa-paper-plane"
          onClick={() => {
            element.attachmentFile.ref?.current?.upload();
          }}
          disabled={component.state.element.attachmentFile.uploading}
        />
      </div>
    </div>
  </div>;

  const quicklyReply = (props.render.quicklyReply.isRender && quicklyReplies.length > 0) && <div className={`${props.component.name}__conversation__quickly-reply ${component.state.element.quicklyReply.show ? 'show' : 'hide'}`}>
    <div className={`${props.component.name}__conversation__quickly-reply-content`}>
      <div className={`${props.component.name}__conversation__quickly-reply-content-header`}>
        <span>Respuestas rápidas</span>
        <i className="pi pi-times" style={{ 'fontSize': '1.5em' }} onClick={() => {component.setState((state) => {state.element.quicklyReply.show = false;})}}></i>
      </div>
      <div className={`${props.component.name}__conversation__quickly-reply-actions`}>
        <NeoInputText
          label='Busca tu mensaje' 
          value={component.state.element.quicklyReply.search}
          onChange={(event) => {
            component.setState((state) => {state.element.quicklyReply.search = event.target.value})
          }}
        />
      </div>
      <div className={`${props.component.name}__conversation__quickly-reply-content-replies`}>
        {component.state.element.quicklyReply.filtered.map(({title, replie}, index) => {
          const text = reactStringReplace(replie, '\n', (match, index) => (
            <br key={index} />
          ));

          return <div 
            key={index}
            className={`${props.component.name}__conversation__quickly-reply-content-replies-replie`}
            onClick={() => {
              component.setState((state) => {
                state.element.quicklyReply.show = false;
                state.element.addMessage.input.value = replie;
              });
              element.addMessage.input.ref?.current?.focus();
            }}
          >
            <p>{title}</p>
            <div>{text}</div>
          </div>
        })
        }
      </div>
    </div>
  </div>;

  const notice = (props.render.notice.isRender) && (() => {
    const finishConversation = (chat.selected.conversation?.isFinished && !chat.selected.conversation?.isReassigned) && 
    <div 
      className={`${props.component.name}__conversation--finished-conversation`}
      ref={element.notice.ref}
    >
      <div className="chat-status-message">
        <NeoTitleMain
          title="Has terminado la conversación"
          subtitle="Ya no tendrás acceso a los mensajes. Puedes continuar con tus demás conversaciones"
        />
      </div>
    </div>;

    const expiredConversation = chat.selected.conversation?.isExpired && 
    <div 
      className={`${props.component.name}__conversation--expired-conversation`}
      ref={element.notice.ref}
    >
      <p>Han pasado más de 24 horas desde el último mensaje.</p>
      <p>Puedes cerrar o reabrir la conversación para seguir hablando con este contacto.</p>
    </div>;

    const reassignedConversation = chat.selected.conversation?.isReassigned && 
    <div
      className={`${props.component.name}__conversation--reassigned-conversation`}
      ref={element.notice.ref}
    >
      <NeoTitleMain
        title="La conversación se reasignó"
      />
    </div>;

    const waitAllowWrie = (chat.selected.conversation?.allowWrite === false) &&
    <div 
      className={`${props.component.name}__conversation--not-allowed-write`}
      ref={element.notice.ref}
    >
      <p>No puedes enviar mensajes hasta que el contacto responda la plantilla enviada.</p>
    </div>;

    const notificationNewMessages = (!chat.selected.conversation?.isFinished && !chat.selected.conversation?.isExpired && !chat.selected.conversation?.isReassigned && Boolean(chat.selected.conversation?.newMessages)) &&
    <div className={`${props.component.name}__conversation--notificationNewMessages`} onClick={contentScrollBottom}>
      <div className={`${props.component.name}__conversation--notificationNewMessages__body`}>
        <span className={`${props.component.name}__conversation--notificationNewMessages__body__count`}>{chat.selected.conversation.newMessages}</span>
        <span className={`${props.component.name}__conversation--notificationNewMessages__body__title`}>Ver mensajes nuevos</span>
      </div>
    </div>;

    return (<>
      {finishConversation || expiredConversation || reassignedConversation || notificationNewMessages || waitAllowWrie}
    </>);
  })();

  const addMessage = (props.render.addMessage.isRender) && (() => {
    const emojis = <ChatActionEmojisItem onEmojiSelect={(emoji) => {
      element.addMessage.input.ref?.current?.addText(emoji.emoji);
      element.addMessage.input.ref?.current?.focus();
    }} />;
    
    const file = <ChatActionAttachmentFileItem onClick={() => {
      component.setState((state) => {
        state.element.attachmentFile.show = true;
      })
    }} />;

    const addQuickReply = (quicklyReplies.length > 0) && <ChatActionQuickReplyItem onClick={() => {
      component.setState((state) => {
        state.element.quicklyReply.show = true;
      })
    }} />;

    const input = <ChatActionInputItem
      value={component.state.element.addMessage.input.value}
      ref={element.addMessage.input.ref}
      onChange={(value) => {
        component.setState((state) => {
          state.element.addMessage.input.value = value;
        })
      }}
      onKeyEnterDown={() => {
        if(component.state.element.addMessage.input.value.trim().length > 0) {
          chat.sendMessage(component.state.element.addMessage.input.value);
          component.setState((state) => {
            state.element.addMessage.input.value = '';
          })
        }
      }}
    />;

    const sendMessage = <ChatActionButtonSendMessageItem onClick={() => {
      if(component.state.element.addMessage.input.value.trim().length > 0) {
        chat.sendMessage(component.state.element.addMessage.input.value);
        component.setState((state) => {
          state.element.addMessage.input.value = '';
        })
      }
    }} />;

    const comment = <ChatContentCommentIconItem onSubmit={chat.sendNewComment} />;

    return (
      <div
        className={`${props.component.name}__conversation__add-message`}
        ref={element.addMessage.ref}
      >
        {emojis}
        {file}
        {addQuickReply}
        {input}
        {sendMessage}
        {comment}
      </div>
    );
  })();

  return (
    <div 
      className={`${props.component.name}__conversation`}
      ref={element.ref}
    >
      {loading}
      {content}
      {attachmentFile}
      {quicklyReply}
      {notice}
      {addMessage}
    </div>
  );
});

export default Conversation;